import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { createMergeTag, deleteMergeTag, getMergeFields, updateMergeTag } from 'api/engage';
import * as DeleteMergeField from 'assets/js/pages/engage/components/Modals/DeleteMergeField';
import { MergeFieldDropdownInput } from 'assets/js/pages/engage/components/Modals/MergeFieldDropdownInput';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import * as Input from 'components/input';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { MergeField } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type MergeFieldFormValues = Pick<MergeField, 'value'> & {
    mergeField: { customFieldId?: number; name: string };
};

const MergeFieldForm = ({
    item,
    children,
}: {
    item?: Omit<MergeField, 'id'> & { id?: number };
    children: React.ReactNode;
}) => {
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const toast = useToast();

    const methods = Form.useGivebutterForm<MergeFieldFormValues>({
        defaultValues: {
            value: item?.value,
            mergeField: {
                name: item?.name,
                customFieldId: undefined,
            },
        },
    });

    const { handleSubmit, watch, setError, control, reset } = methods;
    const mergeField = watch('mergeField');

    const { data: defaultFields } = useQuery({
        queryKey: accountQueryKeys.mergeFields(accountId),
        queryFn: () => getMergeFields(accountId),
        select: data => data.defaults,
    });

    const isDefaultTag = !!defaultFields?.find(defaultItem => defaultItem?.name === item?.name);

    const { mutate: updateOrCreate, isPending } = useMutation({
        mutationFn: (formValue: MergeFieldFormValues) => {
            return item?.id
                ? updateMergeTag(accountId, {
                      value: formValue.value,
                      name: formValue.mergeField.name,
                      id: item.id,
                  })
                : createMergeTag(accountId, {
                      value: formValue.value,
                      name: formValue.mergeField.name,
                      custom_field_id: formValue.mergeField.customFieldId,
                  });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.mergeFields(accountId),
            });

            toast.success(`Merge field default ${item?.id ? 'saved' : 'added'}!`);
            reset(
                !item?.id
                    ? { value: '', mergeField: { name: '' } }
                    : { value: item.value, mergeField: { name: item.name } },
            );
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            const data = error.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to update merge field');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    const message = errors[field][0];
                    if (field === 'name') {
                        setError('mergeField', { message });
                    } else {
                        setError(field as keyof MergeFieldFormValues, {
                            message,
                        });
                    }
                }
            }
        },
    });

    const { mutate: deleteItem } = useMutation({
        mutationFn: (itemId: number) => {
            return deleteMergeTag(accountId, itemId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.mergeFields(accountId),
                type: 'all',
            });
            toast.success('Successfully deleted merge tag default!');
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data.message || (
                    <>
                        <span className={'font-medium'}>Whoops.</span> Something went wrong. Please
                        try again.
                    </>
                ),
            );
        },
    });

    const onSubmit = (formValue: MergeFieldFormValues) => {
        if (item?.id && isDefaultTag && !formValue.value.trim()) {
            deleteItem(item?.id);
            return;
        }
        updateOrCreate(formValue);
    };

    return (
        <Dialog.Root key={key} onOpenChange={open => !open && reset()}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Form.Provider {...methods}>
                        <Dialog.Close />
                        <Dialog.Body className="flex flex-col gap-4">
                            {isPending && <Loader />}
                            <Dialog.Title>
                                {item?.id ? 'Edit default' : 'Add new default'}
                            </Dialog.Title>
                            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8">
                                <div className="flex flex-col gap-5">
                                    <Form.Container>
                                        <Form.Label name="name">Merge field</Form.Label>
                                        <MergeFieldDropdownInput
                                            disabled={isDefaultTag || !!item?.id}
                                            name="mergeField"
                                            control={control}
                                            item={item}
                                        />
                                        <Form.Error name="mergeField" />
                                    </Form.Container>
                                    {mergeField.name && (
                                        <>
                                            <Form.Container>
                                                <Form.Label name="name">Tag</Form.Label>
                                                <Input.Root disabled>
                                                    <Input.Form value={`{{${mergeField.name}}}`} />
                                                </Input.Root>
                                                <p className="text-sm">
                                                    This is the tag to add into your content
                                                </p>
                                            </Form.Container>
                                            <Form.Container>
                                                <Form.Label name="value">Default value</Form.Label>
                                                <Form.TextInputRoot name="value" control={control}>
                                                    <Form.TextInputForm />
                                                </Form.TextInputRoot>
                                                <p className="text-sm">
                                                    This is what is shown when a tag has no data.
                                                </p>
                                                <Form.Error name="value" />
                                            </Form.Container>
                                        </>
                                    )}
                                </div>

                                <div className="flex justify-between">
                                    <div>
                                        {!!item?.id && (
                                            <DeleteMergeField.Root itemId={item.id}>
                                                <DeleteMergeField.Trigger asChild>
                                                    <Button variant="transparent" color="error">
                                                        Delete
                                                    </Button>
                                                </DeleteMergeField.Trigger>
                                            </DeleteMergeField.Root>
                                        )}
                                    </div>

                                    <div className="flex gap-4">
                                        <Dialog.Cancel asChild>
                                            <Button variant="outline" color="neutral">
                                                Cancel
                                            </Button>
                                        </Dialog.Cancel>
                                        <Form.SubmitButton>Save</Form.SubmitButton>
                                    </div>
                                </div>
                            </form>
                        </Dialog.Body>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const Root = MergeFieldForm;
const MergeFieldFormTrigger = Dialog.Trigger;
const Trigger = MergeFieldFormTrigger;

export {
    Root,
    Trigger,
    //
    MergeFieldForm,
    MergeFieldFormTrigger,
};
